import { useCallback } from 'react'

import { Switch, Route } from 'react-router-dom'

import { LoginPage, ProtectedRoute } from '@dentalux/security'

import AnamnesisPage from 'pages/Anamnesis'

import AppShell from './components/AppShell'
import FullPageLoading from './components/FullPageLoading'
import GuardedRoute from './components/GuardedRoute'
import { DOCUMENT_PATH_PREFIX } from './config/flow'
import type { Guard } from './config/routes'
import { routes, preventGoBackAfterFactoringGuard, preventGoBackAfterSuccessGuard } from './config/routes'
import AllergiesPage from './pages/anamnesis/Allergies'
import ConsentsPage from './pages/anamnesis/Consents'
import ContactDetailsPage from './pages/anamnesis/ContactDetails'
import InsurancePage from './pages/anamnesis/Insurance'
import PersonalInformationPage from './pages/anamnesis/PersonalInformation'
import PreConditions1Page from './pages/anamnesis/PreConditions'
import PreConditions2Page from './pages/anamnesis/PreConditions2'
import PreConditions3Page from './pages/anamnesis/PreConditions3'
import PreparationsPage from './pages/anamnesis/PreparationsPage'
import PrintableSummaryPage from './pages/anamnesis/PrintableSummary'
import ReferralPage from './pages/anamnesis/Referral'
import SummaryPage from './pages/anamnesis/Summary'
import Wishes1Page from './pages/anamnesis/Wishes'
import Wishes2Page from './pages/anamnesis/Wishes2'
import Wishes3Page from './pages/anamnesis/Wishes3'
import CodePage from './pages/Code'
import DocumentPage from './pages/Document'
import FactoringAgreementPage from './pages/FactoringAgreement'
import IntroPage from './pages/Intro'
import SuccessPage from './pages/Success'
import { useAnswers } from './providers/AnswersProvider'
import { usePatient } from './providers/PatientProvider'
import { useVisitedSteps } from './providers/VisitedStepsProvider'

const Routes = () => {
  const answers = useAnswers()
  const visitedSteps = useVisitedSteps()
  const patient = usePatient()

  const withGuardParams = useCallback(
    (guard: Guard) => () => guard(answers, patient, visitedSteps),
    [answers, patient, visitedSteps]
  )

  if (patient?.status === 'loading') return <FullPageLoading />

  return (
    <Switch>
      <Route exact path="/login">
        <LoginPage title="Ana" />
      </Route>

      <ProtectedRoute path="/anamnesis/:anamnesisReferenceId">
        <AnamnesisPage />
      </ProtectedRoute>
      <Route component={PrintableSummaryPage} path="/summary/print" exact />

      <GuardedRoute guard={withGuardParams(routes.code.guard)} component={CodePage} path={routes.code.path} exact />

      <GuardedRoute guard={withGuardParams(routes.intro.guard)} component={IntroPage} path={routes.intro.path} exact />

      <Route path="/">
        <AppShell>
          <Switch>
            <GuardedRoute
              guard={withGuardParams(routes.personalInformation.guard)}
              path={routes.personalInformation.path}
              component={PersonalInformationPage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.address.guard)}
              path={routes.address.path}
              component={ContactDetailsPage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.insurance.guard)}
              path={routes.insurance.path}
              component={InsurancePage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.preConditions1.guard)}
              path={routes.preConditions1.path}
              component={PreConditions1Page}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.preConditions2.guard)}
              path={routes.preConditions2.path}
              component={PreConditions2Page}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.preConditions3.guard)}
              path={routes.preConditions3.path}
              component={PreConditions3Page}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.allergies.guard)}
              path={routes.allergies.path}
              component={AllergiesPage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.preparations.guard)}
              path={routes.preparations.path}
              component={PreparationsPage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.wishes.guard)}
              path={routes.wishes.path}
              component={Wishes1Page}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.wishes2.guard)}
              path={routes.wishes2.path}
              component={Wishes2Page}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.wishes3.guard)}
              path={routes.wishes3.path}
              component={Wishes3Page}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.referral.guard)}
              path={routes.referral.path}
              component={ReferralPage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.consents.guard)}
              path={routes.consents.path}
              component={ConsentsPage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.summary.guard)}
              backGuard={withGuardParams(preventGoBackAfterFactoringGuard(routes.summary.path))}
              path={routes.summary.path}
              component={SummaryPage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.success.guard)}
              path={routes.success.path}
              component={SuccessPage}
              exact
            />

            <GuardedRoute
              guard={withGuardParams(routes.factoringAgreement.guard)}
              backGuard={withGuardParams(preventGoBackAfterSuccessGuard(routes.factoringAgreement.path))}
              path={routes.factoringAgreement.path}
              component={FactoringAgreementPage}
              exact
            />

            {/*
              Keyed on the path so every document step gets its own instance. One route serves them all, and
              without the key React keeps the previous step's component mounted when only the param changes: the
              signature stays submitted, so the signature modal never reopens, and the previous document's PDF
              stays on screen until the next one has loaded.
            */}
            <Route
              path={`${DOCUMENT_PATH_PREFIX}/:stepName`}
              exact
              render={({ location }) => <DocumentPage key={location.pathname} />}
            />
          </Switch>
        </AppShell>
      </Route>
    </Switch>
  )
}

export default Routes
